import {
  CampaignAdminModelMarketingStatus,
  CampaignAdminModelStage,
  CampaignDashboardLaunch,
  CampaignDashboardRow,
} from '@urbanx/agx.campaigns.client'
import {
  AgxAvatar,
  AgxAvatarLabelGroup,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTag,
  AgxTooltip,
  AgxTypography,
  IColumnConfig,
  SortOrder,
} from '@urbanx/agx.ui-components2'
import { cleanTwoLineAddress, PropertyAddress } from '@urbanx/agx-ui-components'
import dayjs from 'dayjs'
import placeHolderImage from 'assets/images/placeholder-property.png'
import { useMemo } from 'react'
import { getAgencyInitials } from 'utils/agencyUtils'
import { formatCampaignStageLabel } from 'utils/campaignUtils'
import { getStatusConfig } from '../components/campaignMarketing/CampaignFlowStatusBadge'
import { AgencyLookupValue } from './useCampaignsDashboard'
import slugify from 'slugify'

const formatTaskStageLabel = (stage: string) =>
  stage.replace(/([a-z])([A-Z])/g, '$1 $2')

interface TaskOverflowTagProps {
  id: string
  remainingTaskCount: number
  tooltip: string
}

const TaskOverflowTag = ({
  id,
  remainingTaskCount,
  tooltip,
}: TaskOverflowTagProps) => {
  return (
    <AgxTooltip id={id} content={tooltip}>
      <AgxTag id={id} color="var(--text-black)">
        {`+${remainingTaskCount}`}
      </AgxTag>
    </AgxTooltip>
  )
}

const formatLaunchBadge = (launch?: CampaignDashboardLaunch) => {
  if (!launch?.status) {
    return (
      <AgxBadge
        type="colourfill"
        variant="normal"
        size="md"
        text="Launch not set"
        colour="grey"
      />
    )
  }

  if (launch.status === 'OffMarket') {
    return (
      <AgxBadge
        type="colourfill"
        variant="normal"
        size="md"
        text="Off market"
        colour="grey"
      />
    )
  }

  if (!launch.date) {
    return (
      <AgxBadge
        type="colourfill"
        variant="normal"
        size="md"
        text={
          launch.status === 'ToBeConfirmed' ? 'Launch TBC' : 'Launch confirmed'
        }
        colour="yellow"
      />
    )
  }

  const launchDate = dayjs(launch.date)
  const launchDateToday = launchDate.isSame(dayjs(), 'day')
  const isLaunched = launchDate.isBefore(dayjs(), 'day')

  if (launchDateToday) {
    return (
      <AgxBadge
        type="highlighted"
        variant="normal"
        size="md"
        highlightText="Today"
        text={`Launch ${launchDate.format('Do MMM')}`}
        colour="yellow"
      />
    )
  }

  return (
    <AgxBadge
      type="colourfill"
      variant="normal"
      size="md"
      text={`${isLaunched ? 'Launched' : 'Launch'} ${launchDate.format('Do MMM')}`}
      colour={isLaunched ? 'grey' : 'yellow'}
    />
  )
}

interface CampaignsDashboardTableProps {
  rows: CampaignDashboardRow[]
  agencyLookup: Record<string, AgencyLookupValue>
  isPending: boolean
  isAgenciesPending: boolean
  lastRowRef: (node: HTMLDivElement | null) => void
  onCampaignRowClick: (index: number) => void
  onSortLaunch: (sortOrder: SortOrder) => void
}

const CampaignsDashboardTable = ({
  rows,
  agencyLookup,
  isPending,
  isAgenciesPending,
  lastRowRef,
  onCampaignRowClick,
  onSortLaunch,
}: CampaignsDashboardTableProps) => {
  const dashboardColumns: (string | IColumnConfig)[] = [
    'Address',
    'Agency',
    'Status',
    'Tasks',
    'Contract',
    {
      name: 'Launch',
      sortable: true,
      sortFunction: onSortLaunch,
      defaultSortOrder: 1,
    },
    { align: 'right', name: 'Marketing' },
  ]

  const tableRows = useMemo(() => {
    return rows.map((row, index) => {
      const agencyName = row.agencyId
        ? agencyLookup[row.agencyId]?.name
        : undefined
      const agencyInitials = getAgencyInitials(agencyName)
      const cleanAddress = row.address
        ? cleanTwoLineAddress(row.address as PropertyAddress)
        : ['Unknown address']
      const [addressLineOne, addressLineTwo] = Array.isArray(cleanAddress)
        ? cleanAddress
        : [cleanAddress]
      const marketingStatusConfig = getStatusConfig(
        row.marketingStatus as CampaignAdminModelMarketingStatus | undefined
      )
      const isLastRow = index === rows.length - 1
      const taskStages = row.taskStages ?? []
      const visibleTaskStages = taskStages.slice(0, 2)
      const remainingTaskStages = taskStages.slice(visibleTaskStages.length)
      const remainingTaskCount = remainingTaskStages.length
      const remainingTaskStagesTooltip = remainingTaskStages
        .map((taskStage) => formatTaskStageLabel(taskStage))
        .join('\n')

      return {
        Address: (
          <div ref={isLastRow ? lastRowRef : null}>
            <AgxSpacer contentAlignment="start">
              <AgxAvatarLabelGroup
                id={`campaign-dashboard-${index}`}
                label1={addressLineOne ?? 'Unknown address'}
                label2={addressLineTwo ?? ''}
                photoUrl={row.propertyImageUrl ?? placeHolderImage}
                type="avatar"
                variant="custom"
                photoHeight="48px"
                photoWidth="64px"
                size="large"
              />
            </AgxSpacer>
          </div>
        ),
        Agency: (
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            contentAlignment="center"
          >
            {row.agencyId && agencyLookup[row.agencyId]?.logoUrl && (
              <AgxAvatar
                type="image"
                size="sm"
                imageUri={agencyLookup[row.agencyId]?.logoUrl ?? ''}
              />
            )}
            {row.agencyId && !agencyLookup[row.agencyId]?.logoUrl && (
              <AgxAvatar
                type="text-dark"
                size="sm"
                text={agencyInitials ?? 'UA'}
              />
            )}

            <AgxTypography
              id={`campaign-dashboard-agency-${index}`}
              size="small"
              type="body_ui"
              color="var(--text-secondary)"
            >
              {agencyName ?? '--'}
            </AgxTypography>
          </AgxSpacer>
        ),
        Status: (
          <AgxTypography
            id={`campaign-dashboard-status-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {row.campaignStatus
              ? formatCampaignStageLabel(
                  row.campaignStatus as CampaignAdminModelStage
                )
              : '--'}
          </AgxTypography>
        ),
        Tasks: row.taskStages?.length ? (
          <AgxSpacer direction="horizontal" className="tasksTagContainer">
            {visibleTaskStages.map((taskStage) => (
              <AgxTag
                key={slugify(taskStage)}
                id={`campaign-dashboard-tasks-${index}-${slugify(taskStage)}`}
                color="var(--text-primary)"
              >
                {formatTaskStageLabel(taskStage)}
              </AgxTag>
            ))}
            {remainingTaskCount > 0 && (
              <TaskOverflowTag
                id={`campaign-dashboard-tasks-${index}-remaining`}
                remainingTaskCount={remainingTaskCount}
                tooltip={remainingTaskStagesTooltip}
              />
            )}
          </AgxSpacer>
        ) : (
          '--'
        ),
        Marketing: marketingStatusConfig ? (
          <AgxBadge
            type="colourfill"
            colour={marketingStatusConfig.badgeColor}
            size="md"
            text={marketingStatusConfig.text}
          />
        ) : (
          '--'
        ),
        Contract: row.blankContractLive ? (
          <AgxBadge
            type="colourfill"
            colour="green"
            size="md"
            text="Contract Live"
          />
        ) : (
          <AgxBadge
            type="colourfill"
            colour="red"
            size="md"
            text="Contract Offline"
          />
        ),
        Launch: formatLaunchBadge(row.launch),
      }
    })
  }, [agencyLookup, lastRowRef, rows])

  return (
    <AgxSpacer direction="horizontal" fill>
      <AgxSpacer direction="vertical" gap={'sm'} fill>
        <AgxTable
          columns={dashboardColumns}
          rows={tableRows}
          variant={!isPending && tableRows.length ? 'default' : 'minimal'}
          showLoadingState={isPending && tableRows.length === 0}
          tableStyle="square"
          noDataLabel={
            isAgenciesPending || isPending
              ? 'Loading...'
              : 'No campaigns found.'
          }
          noDataGlyphType="house"
          onRowClick={
            !isPending && tableRows.length ? onCampaignRowClick : undefined
          }
        />
      </AgxSpacer>
    </AgxSpacer>
  )
}

export default CampaignsDashboardTable
